import { useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  getEmailJsPublicKey,
  getEmailJsServiceId,
  initEmailJs,
  emailJsSendFormOptions,
  emailJsEnvLooksMisconfigured,
} from "../lib/emailjsConfig";

const initialState = {
  name: "",
  email: "",
  message: "",
};

initEmailJs();

export const Contact = (props) => {
  const [{ name, email, message }, setState] = useState(initialState);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const clearState = () => setState({ ...initialState });

  const handleSubmit = (e) => {
    e.preventDefault();

    const pk = getEmailJsPublicKey();
    const sid = getEmailJsServiceId();

    if (!pk || !sid) {
      setSubmitStatus({
        type: "error",
        message: emailJsEnvLooksMisconfigured()
          ? "EmailJS is still set to Azure pipeline placeholders (#{…}#). Put your real Service ID and Public Key in .env or .local_env, then restart npm start."
          : "Email service is not configured. Please contact the administrator or email us directly at info@bytheapp.com",
      });
      return;
    }

    const form = e.target;
    let toEmailInput = form.querySelector('input[name="to_email"]');
    if (!toEmailInput) {
      toEmailInput = document.createElement("input");
      toEmailInput.type = "hidden";
      toEmailInput.name = "to_email";
      form.appendChild(toEmailInput);
    }
    toEmailInput.value = "info@bytheapp.com";

    setSubmitStatus({ type: "", message: "" });
    setIsSubmitting(true);

    emailjs
      .sendForm(sid, "template_he45gmv", form, emailJsSendFormOptions())
      .then(
        () => {
          setSubmitStatus({
            type: "success",
            message: "Message sent successfully! We will get back to you soon.",
          });
          clearState();
          setTimeout(() => setSubmitStatus({ type: "", message: "" }), 5000);
        },
        (error) => {
          console.error("EmailJS Error:", error);
          setSubmitStatus({
            type: "error",
            message:
              "Failed to send message. Please try again or email us directly at info@bytheapp.com",
          });
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  const linkedinUrl =
    props.data?.linkedin ?? "https://www.linkedin.com/company/bytheapp";

  return (
    <div>
      <div id="contact" className="contact-section">
        <div className="container">
          <div className="contact-header">
            <span className="contact-eyebrow">Get In Touch</span>
            <h2 className="contact-title">We&apos;d love to hear from you</h2>
            <p className="contact-lead">
              Send us a message and our team will get back to you as soon as
              possible.
            </p>
          </div>

          <div className="contact-layout">
            <div className="contact-form-card">
              <form name="sentMessage" onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="name" className="contact-label">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control contact-input"
                        placeholder="Your name"
                        required
                        value={name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="email" className="contact-label">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control contact-input"
                        placeholder="you@company.com"
                        required
                        value={email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="message" className="contact-label">
                    Message
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    className="form-control contact-input"
                    rows="5"
                    placeholder="How can we help?"
                    required
                    value={message}
                    onChange={handleChange}
                  />
                </div>
                {submitStatus.message && (
                  <div
                    className={`contact-alert ${submitStatus.type === "success" ? "contact-alert-success" : "contact-alert-error"}`}
                    role="alert"
                  >
                    {submitStatus.message}
                  </div>
                )}
                <button
                  type="submit"
                  className="btn btn-custom btn-lg contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            </div>

            <aside className="contact-sidebar">
              <div className="contact-details-card">
                <span className="contact-details-label">Contact</span>
                {props.data?.phone && (
                  <div className="contact-detail">
                    <div className="contact-detail-icon" aria-hidden="true">
                      <i className="fa fa-phone" />
                    </div>
                    <div>
                      <span className="contact-detail-title">Phone</span>
                      <a href={`tel:${props.data.phone.replace(/\s/g, "")}`}>
                        {props.data.phone}
                      </a>
                    </div>
                  </div>
                )}
                {props.data?.email && (
                  <div className="contact-detail">
                    <div className="contact-detail-icon" aria-hidden="true">
                      <i className="fa fa-envelope-o" />
                    </div>
                    <div>
                      <span className="contact-detail-title">Email</span>
                      <a href={`mailto:${props.data.email}`}>
                        {props.data.email}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className="contact-social-card">
                <span className="contact-details-label">Connect</span>
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-linkedin"
                  aria-label="ByTheApp on LinkedIn"
                >
                  <i className="fa fa-linkedin" aria-hidden="true" />
                  <span>Follow us on LinkedIn</span>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div id="footer">
        <div className="container text-center">
          <p>
            &copy; 2026 ByTheApp. All rights reserved.{" "}
            <a href="mailto:info@bytheapp.com">info@bytheapp.com</a>
            {" · "}
            <Link to="/blog">Blog</Link>
            {" · "}
            <Link to="/integrations">Integrations</Link>
            {" · "}
            <Link to="/privacy">Privacy Policy</Link>
            {" · "}
            <Link to="/terms">Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
